import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card,
  PageHeader,
  GhostBtn,
  GoldBtn,
  OutlineBtn,
  SectionTitle,
  Field,
  Input,
  Textarea,
} from "../components/common/Ui";

const HomeClientSuccess = () => {
  // ── Section heading + description ──
  const [meta, setMeta] = useState({ heading: "", description: "" });
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaForm, setMetaForm] = useState({ heading: "", description: "" });
  const [metaSaving, setMetaSaving] = useState(false);

 const loadMeta = () => {
  setMetaLoading(true);
  apiFetch("/home/clientstories.php?type=meta&admin=true")
    .then((res) => setMeta({ heading: res.data?.heading || "", description: res.data?.description || "" }))
    .catch(() => {})
    .finally(() => setMetaLoading(false));
};

  useEffect(loadMeta, []);

  const openMetaEdit = () => {
    setMetaForm(meta);
    setMetaOpen(true);
  };

  const saveMeta = async () => {
    if (!metaForm.heading.trim()) return;
    setMetaSaving(true);
    try {
      await apiFetch("/home/clientstories.php?type=meta", {
        method: "PUT",
        body: { heading: metaForm.heading, description: metaForm.description },
      });
      setMeta(metaForm);
      setMetaOpen(false);
    } catch (err) {
      alert(err.message || "Failed to save section content.");
    } finally {
      setMetaSaving(false);
    }
  };

  // ── Story cards ──
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const mapRow = (r) => ({ id: r.id, title: r.title, image: r.image_url });

 const loadStories = () => {
  setLoading(true);
  setLoadError("");
  apiFetch("/home/clientstories.php?admin=true")
    .then((res) => setStories((res.data || []).map(mapRow)))
    .catch((err) => setLoadError(err.message || "Failed to load stories."))
    .finally(() => setLoading(false));
};

  useEffect(loadStories, []);

  const openEdit = (story) => {
    setForm(story);
    setNewFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(null);
    setNewFile(null);
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewFile(file);
    setForm((f) => ({ ...f, image: URL.createObjectURL(file) }));
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("id", form.id);
      fd.append("title", form.title);
      if (newFile) fd.append("image", newFile);

      await apiFetch("/home/clientstories.php", { method: "POST", body: fd });
      closeForm();
      loadStories();
    } catch (err) {
      alert(err.message || "Failed to save story.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6" style={{ background: T.bg, minHeight: "100vh" }}>
      <PageHeader
        title="Home Client Success"
        subtitle='Manage the "Client Success Stories" section on the homepage'
      />

      {/* ── Section content ── */}
      <Card className="p-4 sm:p-6 mb-5 sm:mb-6">
        <div className="flex items-center justify-between mb-4">
          <SectionTitle>Section Content</SectionTitle>
        </div>
        {metaLoading ? (
          <p className="text-[12px]" style={{ color: T.textSec }}>Loading…</p>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <p className="text-[16px] font-bold italic" style={{ color: T.textPri, fontFamily: T.serif }}>{meta.heading}</p>
              <p className="text-[12px] mt-2 max-w-2xl leading-relaxed" style={{ color: T.textSec }}>{meta.description}</p>
            </div>
            <GhostBtn onClick={openMetaEdit}>Edit</GhostBtn>
          </div>
        )}
      </Card>

      {/* ── Story cards ── */}
      <Card className="p-4 sm:p-6">
        <SectionTitle>Story Cards</SectionTitle>

        {loading ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.textSec }}>Loading…</p>
        ) : loadError ? (
          <p className="text-[12px] py-10 text-center" style={{ color: T.danger }}>{loadError}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {stories.map((s) => (
              <div key={s.id} className="rounded-2xl overflow-hidden flex flex-col" style={{ border: `1px solid ${T.border}` }}>
                <div className="aspect-[4/3] flex items-center justify-center" style={{ background: T.bgSoft }}>
                  {s.image ? (
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[11px]" style={{ color: T.textSec }}>No photo</span>
                  )}
                </div>

                <div className="p-3.5 flex-1">
                  <p className="text-[14px] font-medium italic leading-snug" style={{ color: T.textPri, fontFamily: T.serif }}>{s.title || "Untitled"}</p>
                </div>

                <div className="p-2.5" style={{ borderTop: `1px solid ${T.border}` }}>
                  <GhostBtn onClick={() => openEdit(s)}>Edit</GhostBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Edit section content modal ── */}
      {metaOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !metaSaving && setMetaOpen(false)}>
          <div className="rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" style={{ background: T.card }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h3 className="text-[15px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Edit Section Content</h3>
              <button onClick={() => setMetaOpen(false)} className="text-[13px] font-medium" style={{ color: T.textSec }}>✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <Field label="Heading" required>
                <Input value={metaForm.heading} onChange={(e) => setMetaForm((f) => ({ ...f, heading: e.target.value }))} placeholder="e.g. Client Success Stories" />
              </Field>
              <Field label="Description">
                <Textarea rows={5} value={metaForm.description} onChange={(e) => setMetaForm((f) => ({ ...f, description: e.target.value }))} placeholder="Short paragraph shown next to the heading" />
              </Field>
            </div>

            <div className="flex items-center gap-2.5 p-5 flex-shrink-0" style={{ borderTop: `1px solid ${T.border}` }}>
              <OutlineBtn onClick={() => setMetaOpen(false)}>Cancel</OutlineBtn>
              <GoldBtn onClick={saveMeta}>{metaSaving ? "Saving…" : "Save Changes"}</GoldBtn>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit story card modal — image / title only ── */}
      {formOpen && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={closeForm}>
          <div className="rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" style={{ background: T.card }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h3 className="text-[15px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Edit Story</h3>
              <button onClick={closeForm} className="text-[13px] font-medium" style={{ color: T.textSec }}>✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <Field label="Photo">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
                    {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <span className="text-[9px] text-center px-1" style={{ color: T.textSec }}>No photo</span>}
                  </div>
                  <label className="flex items-center justify-center gap-1.5 rounded-xl py-2.5 px-4 text-[12px] font-semibold cursor-pointer transition-all flex-1" style={{ border: `1.5px dashed ${T.gold}`, color: T.gold }}>
                    Choose from gallery
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoPick} />
                  </label>
                </div>
              </Field>

              <Field label="Title" required>
                <Textarea rows={2} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Muse Launches Revolutionary Product with Surprise Gift Hampers" />
              </Field>
            </div>

            <div className="flex items-center gap-2.5 p-5 flex-shrink-0" style={{ borderTop: `1px solid ${T.border}` }}>
              <OutlineBtn onClick={closeForm}>Cancel</OutlineBtn>
              <GoldBtn onClick={handleSave}>{saving ? "Saving…" : "Save Changes"}</GoldBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeClientSuccess;